/**
 * 업종별 산재보험료 환급 SEO 랜딩 (/insurance-refund/:slug, /en/insurance-refund/:slug).
 * 프로그래매틱 SEO: 업종마다 요율·예시·키워드가 달라 고유 콘텐츠. 무LLM(요율 계산만).
 */
import { useEffect, useMemo, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { useTranslation } from 'react-i18next'
import { Link, Navigate, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { motion } from 'framer-motion'
import { FiTrendingDown, FiArrowRight, FiAlertTriangle, FiSearch, FiArrowLeft } from 'react-icons/fi'
import ForceDarkTheme from '../../components/common/ForceDarkTheme'
import Button from '../../components/ui/Button'
import Card, { CardBody } from '../../components/ui/Card'
import Spinner from '../../components/ui/Spinner'
import { IR_I18N, type IrLang } from './irI18n'
import { IR_INDUSTRIES, findIndustry } from './irArchive'
import { trackView } from '../../lib/trackView'
import { irMeta, irEstimatePublic, type IrEstimate } from '../../lib/insuranceRefundApi'
import LeadCaptureBox from './LeadCaptureBox'

const SIGNUP_PATH = '/signup'
const HOST = 'https://heinrich.dasancms.co.kr'

export default function InsuranceRefundIndustry() {
  const { slug = '' } = useParams()
  const { i18n } = useTranslation()
  const forcedEn = typeof window !== 'undefined' && window.location.pathname.startsWith('/en/')
  const lang: IrLang = forcedEn || i18n.language?.startsWith('en') ? 'en' : 'ko'
  const s = IR_I18N[lang]
  const ind = findIndustry(slug)

  const metaQ = useQuery({ queryKey: ['ir-meta'], queryFn: irMeta })
  const [workers, setWorkers] = useState('20')
  const [wage, setWage] = useState('')
  const [result, setResult] = useState<IrEstimate | null>(null)

  const wageNum = wage === '' ? (metaQ.data?.default_monthly_wage ?? 3000000) : Number(wage)
  const canCalc = !!ind && Number(workers) >= 1 && Number(workers) < 50 && wageNum > 0

  // 유입 귀속 — 이 업종 랜딩(공유 도착지)으로 들어와 가입하면 출처가 slug 단위로 잡힌다.
  useEffect(() => { if (slug) trackView('insurance-refund', slug) }, [slug])

  // 진입 시 기본값(20인)으로 예시 자동 계산 → 즉시 금액 노출
  useEffect(() => {
    if (!ind || !metaQ.data) return
    irEstimatePublic({ industry_key: ind.key, worker_count: 20, monthly_wage_per_capita: metaQ.data.default_monthly_wage })
      .then(setResult).catch(() => {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ind?.key, metaQ.data?.default_monthly_wage])

  async function recalc() {
    if (!canCalc || !ind) return
    try {
      setResult(await irEstimatePublic({ industry_key: ind.key, worker_count: Number(workers), monthly_wage_per_capita: wageNum }))
    } catch { /* 조용히 무시 */ }
  }

  const won = s.won
  const koUrl = `${HOST}/insurance-refund/${slug}/`
  const enUrl = `${HOST}/en/insurance-refund/${slug}/`
  const canonical = lang === 'en' ? enUrl : koUrl

  const related = useMemo(() => IR_INDUSTRIES.filter((i) => i.slug !== slug).slice(0, 8), [slug])

  if (metaQ.isSuccess && !ind) return <Navigate to="/insurance-refund" replace />

  const label = ind ? ind.label[lang] : ''
  const title = lang === 'en'
    ? `${label} — Industrial-accident insurance 20% refund calculator | Heinrich`
    : `${label} 산재보험료 20% 환급 계산기 · 50인 미만 요율제 | Heinrich`
  const desc = ind
    ? (lang === 'en'
        ? `${label} workplaces under 50 workers can cut industrial-accident insurance premiums up to 20% for 3 years. Estimate your savings free.`
        : `${label} 50인 미만 사업장은 위험성평가 인정으로 산재보험료를 3년간 최대 20% 아낄 수 있습니다. 예상 절감액을 무료로 계산하세요.`)
    : ''

  const faqLd = ind ? {
    '@context': 'https://schema.org', '@type': 'FAQPage',
    mainEntity: s.faq.map((f) => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })),
  } : null

  return (
    <>
      <ForceDarkTheme />
      <Helmet>
        <html lang={lang} />
        <title>{title}</title>
        <meta name="description" content={desc} />
        {ind && <meta name="keywords" content={ind.keywords.join(', ')} />}
        <link rel="canonical" href={canonical} />
        <link rel="alternate" hrefLang="ko" href={koUrl} />
        <link rel="alternate" hrefLang="en" href={enUrl} />
        <link rel="alternate" hrefLang="x-default" href={koUrl} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={desc} />
        {faqLd && <script type="application/ld+json">{JSON.stringify(faqLd)}</script>}
      </Helmet>

      {!ind ? (
        <div className="flex justify-center py-24"><Spinner size="lg" /></div>
      ) : (
        <div className="mx-auto max-w-4xl px-4 py-10 sm:py-14">
          <Link to="/insurance-refund" className="inline-flex items-center gap-1 text-[13px] text-ink-muted hover:text-ink">
            <FiArrowLeft size={13} /> {lang === 'en' ? 'Refund calculator' : '산재보험료 환급 계산기'}
          </Link>

          {/* HERO */}
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="mt-4">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-500/12 px-3 py-1 text-xs font-semibold text-blue-300">
              <FiTrendingDown size={13} /> {s.badge}
            </span>
            <h1 className="mt-3 text-2xl font-bold leading-tight text-ink sm:text-3xl">
              {label}, <span className="bg-gradient-to-r from-blue-400 to-emerald-400 bg-clip-text text-transparent">{s.heroHighlight}</span>
            </h1>
            <p className="mt-3 max-w-2xl text-[15px] leading-relaxed text-ink-muted">{ind.intro[lang]}</p>
          </motion.div>

          {/* 예시 절감액 + 계산 */}
          <Card className="mt-6 border-blue-500/25">
            <CardBody className="p-6">
              {result && (
                <div className="rounded-xl bg-gradient-to-br from-blue-500/10 to-emerald-500/10 p-5 text-center">
                  <div className="text-xs text-ink-muted">{s.savingsLead} ({result.worker_count}{s.workerUnit || (lang === 'en' ? ' workers' : '명')} · {lang === 'en' ? 'rate' : '요율'} {result.rate_pct}%)</div>
                  <div className="mt-1 bg-gradient-to-r from-blue-400 to-emerald-400 bg-clip-text text-4xl font-extrabold text-transparent">{won(result.headline_savings)}</div>
                  <div className="mt-1 text-[12px] text-ink-faint">{s.premiumNote(won(result.annual_premium))}</div>
                </div>
              )}
              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                <label className="block">
                  <span className="mb-1 block text-xs font-medium text-ink-muted">{s.workerLabel}</span>
                  <input type="number" min={1} max={49} value={workers} onChange={(e) => setWorkers(e.target.value)}
                    className="w-full rounded-lg border border-surface-elevated/60 bg-surface-card px-3 py-2 text-sm text-ink focus:border-blue-400 focus:outline-none" />
                </label>
                <label className="block">
                  <span className="mb-1 block text-xs font-medium text-ink-muted">{s.wageLabel}</span>
                  <input type="text" inputMode="numeric" value={wage ? Number(wage).toLocaleString('ko-KR') : ''}
                    placeholder={Number(metaQ.data?.default_monthly_wage ?? 3000000).toLocaleString('ko-KR')}
                    onChange={(e) => setWage(e.target.value.replace(/[^0-9]/g, ''))}
                    className="w-full rounded-lg border border-surface-elevated/60 bg-surface-card px-3 py-2 text-sm text-ink focus:border-blue-400 focus:outline-none" />
                </label>
              </div>
              <Button onClick={recalc} disabled={!canCalc} className="mt-3">{s.calcBtn}</Button>
            </CardBody>
          </Card>

          {/* 업종 안전 포인트 + 사고사례 연결 */}
          <div className="mt-4 flex flex-col gap-2 rounded-xl border border-amber-500/20 bg-amber-500/[0.04] p-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-2 text-[13px]">
              <FiAlertTriangle className="mt-0.5 shrink-0 text-amber-400" size={15} />
              <span className="text-ink-muted"><b className="text-amber-300">{lang === 'en' ? 'Common hazards' : '이 업종 안전 포인트'}</b> · {ind.safetyNote[lang]}</span>
            </div>
            <Link to={`/ac-datacenter?q=${encodeURIComponent(ind.acQuery)}`} className="shrink-0">
              <Button size="sm" variant="secondary"><FiSearch className="mr-1.5" size={13} />{lang === 'en' ? 'See real accidents' : '실제 사고사례 보기'}</Button>
            </Link>
          </div>

          {/* CTA */}
          <div className="mt-6 flex flex-col items-center gap-3 rounded-2xl bg-gradient-to-br from-blue-500/10 to-emerald-500/10 p-6 text-center">
            <div className="text-base font-bold text-ink">{s.ctaTitle}</div>
            <p className="max-w-xl text-sm text-ink-muted">{s.ctaBody}</p>
            <Link to={SIGNUP_PATH}><Button>{s.ctaBtn} <FiArrowRight className="ml-1.5" /></Button></Link>
          </div>

          {/* 리드 캡처 */}
          <div className="mt-4">
            <LeadCaptureBox lang={lang} source={`insurance-refund-${slug}`} />
          </div>

          {/* 업종 상호링크 (SEO) */}
          <div className="mt-8">
            <div className="mb-2 text-sm font-semibold text-ink">{lang === 'en' ? 'Other industries' : '다른 업종 보기'}</div>
            <div className="flex flex-wrap gap-2">
              {related.map((r) => (
                <Link key={r.slug} to={`${forcedEn ? '/en' : ''}/insurance-refund/${r.slug}`}
                  className="rounded-full border border-surface-elevated/50 bg-surface-card px-3 py-1.5 text-[12px] text-ink-muted hover:border-blue-400 hover:text-ink">
                  {r.label[lang]}
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  )
}
